import { COMMAND_TYPE } from "../master/sync/commandTypes";
import { getCalendarCommandQueue } from "../master/sync/commandQueueRegistry";
import { ultraSessionLog } from "../debug/ultraSessionDebug";
import { normalizeMutationFailure } from "./normalizeMutationFailure";

let draining = false;
const queue = [];

/**
 * Enqueue an async calendar mutation. Operations run one at a time, in order.
 * @param {() => Promise<unknown>} fn
 * @returns {Promise<unknown>}
 */
export function enqueueCalendarMutation(fn, options = {}) {
   const label = options?.label || options?.type || "mutation";
   ultraSessionLog("QUEUE", "enqueue", { label });
   const commandQueue = getCalendarCommandQueue();
   if (commandQueue) {
      const { buildUndoEntry = null } = options || {};
      const t0 = performance.now?.() ?? Date.now();
      return commandQueue
         .enqueue(
            COMMAND_TYPE.CREATE,
            {
               run: async () => {
                  const result = await fn();
                  if (result && typeof result === "object" && "ok" in result) {
                     return result;
                  }
                  return { ok: true, ...(result || {}) };
               },
               ...(typeof buildUndoEntry === "function" ? { buildUndoEntry } : {}),
            },
            null,
         )
         .then((command) => {
            const ms = (performance.now?.() ?? Date.now()) - t0;
            const result = command?.result;
            if (result && typeof result === "object" && result.ok === false) {
               ultraSessionLog(
                  "WARN",
                  "queue:failed",
                  { label, error: normalizeMutationFailure(result.error) },
                  { durationMs: ms },
               );
               return result;
            }
            ultraSessionLog(
               "QUEUE",
               "done",
               { label, ok: command?.result?.ok !== false },
               { durationMs: ms },
            );
            return result;
         })
         .catch((error) => {
            const ms = (performance.now?.() ?? Date.now()) - t0;
            const message = normalizeMutationFailure(error);
            ultraSessionLog(
               "WARN",
               "queue:commandFailed",
               { label, error: message },
               { durationMs: ms },
            );
            return { ok: false, error: message };
         });
   }

   return new Promise((resolve, reject) => {
      queue.push({ fn, resolve, reject, label });
      void drainQueue();
   });
}

async function drainQueue() {
   if (draining) return;
   draining = true;
   while (queue.length > 0) {
      const { fn, resolve, reject, label } = queue.shift();
      const t0 = performance.now?.() ?? Date.now();
      try {
         resolve(await fn());
         const ms = (performance.now?.() ?? Date.now()) - t0;
         ultraSessionLog("QUEUE", "done", { label: label || "fallback" }, { durationMs: ms });
      } catch (error) {
         ultraSessionLog("WARN", "queue:error", { label: label || "fallback", message: String(error?.message || error) });
         reject(error);
      }
   }
   draining = false;
}

/** @internal test helper */
export function __resetCalendarMutationQueueForTests() {
   queue.length = 0;
   draining = false;
}
